/* One configured order, with an explicit production breakdown. LPO still owns pricing and uploads. */
(() => {
  'use strict';
  const rules = {
    'jacquard-knit-socks': 'size', 'terry-socks': 'size',
    'embroidered-cuffed-beanies-bc045': 'colour',
    'embroidered-cuffed-beanies-bb830n': 'colour', 'premium-mesh-trucker-caps': 'colour'
  };
  const sizeNames = {'5-7UK': 'UK 5–7', '7-10UK': 'UK 7–10', '10-12K': 'UK 10–12'};
  const property = kind => kind === 'size' ? 'Size quantities' : 'Colour quantities';
  const analyse = (items, kind, tiers = []) => {
    const minimum = kind === 'size' ? 30 : 10;
    const invalid = items.find(item => !Number.isSafeInteger(item.q) || item.q < 0 || item.q > 99999 || (kind === 'size' && item.q > 0 && item.q < 30));
    const total = items.reduce((sum, item) => sum + (Number.isSafeInteger(item.q) ? item.q : 0), 0);
    const missing = minimum - total;
    const unit = kind === 'size' ? (missing === 1 ? 'pair' : 'pairs') : (missing === 1 ? 'item' : 'items');
    const error = invalid ? (kind === 'size' ? 'Enter 30 pairs or more for each chosen size, or 0 to leave it out.' : 'Enter a whole number of 0 or more for each colour.') : total < minimum ? `Add ${missing} more ${unit} to reach the minimum of ${minimum}.` : '';
    return {total, error, tier: [...tiers].sort((a, b) => a - b).filter(tier => tier <= total).pop() || null};
  };
  const describe = (items, kind) => items.filter(item => item.q > 0).map(item => `${item.label}: ${item.q}${kind === 'size' ? ' pairs' : ''}`).join('; ');
  const encode = (items, kind, id) => JSON.stringify({v: 1, kind, ...(id ? {id} : {}), items: items.filter(item => kind === 'size' || item.q > 0)});
  // Preserve chosen colours/sizes and their proportions when a total tile is selected.
  const allocateTotal = (items, kind, target) => {
    const minimum = kind === 'size' ? 30 : 1;
    const selected = items.map((item, index) => ({index, q: item.q})).filter(item => item.q > 0);
    if (!Number.isSafeInteger(target) || target < (kind === 'size' ? 30 : 10) || !selected.length || target < selected.length * minimum || items.some(item => !Number.isSafeInteger(item.q) || item.q < 0)) return null;
    const total = selected.reduce((sum, item) => sum + item.q, 0);
    const shares = selected.map(item => ({...item, ideal: item.q * target / total, value: Math.max(minimum, Math.floor(item.q * target / total))}));
    let remaining = target - shares.reduce((sum, item) => sum + item.value, 0);
    while (remaining) {
      const increasing = remaining > 0;
      const eligible = shares.filter(item => increasing || item.value > minimum);
      eligible.sort((a, b) => increasing ? (b.ideal - b.value) - (a.ideal - a.value) || a.index - b.index : (b.value - b.ideal) - (a.value - a.ideal) || a.index - b.index);
      eligible[0].value += increasing ? 1 : -1;
      remaining += increasing ? -1 : 1;
    }
    return items.map((item, index) => shares.find(share => share.index === index)?.value || 0);
  };
  function enableColourDrag(rows) {
    let drag = null;
    let dragged = false;
    rows.addEventListener('dragstart', event => {
      if (event.target.closest('.pp-colour-order__photo')) event.preventDefault();
    });
    rows.addEventListener('pointerdown', event => {
      dragged = false;
      // Touch keeps the browser's native horizontal swipe and vertical page scroll.
      if (event.pointerType !== 'mouse' || event.button !== 0 || !event.target.closest('.pp-colour-order__photo')) return;
      drag = {pointer: event.pointerId, start: event.clientX, scroll: rows.scrollLeft};
    });
    rows.addEventListener('pointermove', event => {
      if (!drag || event.pointerId !== drag.pointer) return;
      const distance = event.clientX - drag.start;
      if (!dragged && Math.abs(distance) < 7) return;
      dragged = true;
      rows.classList.add('is-dragging');
      if (!rows.hasPointerCapture(drag.pointer)) rows.setPointerCapture(drag.pointer);
      event.preventDefault();
      rows.scrollLeft = drag.scroll - distance;
    });
    const end = event => {
      if (!drag || event.pointerId !== drag.pointer) return;
      const pointer = drag.pointer; drag = null;
      rows.classList.remove('is-dragging');
      if (rows.hasPointerCapture(pointer)) rows.releasePointerCapture(pointer);
    };
    ['pointerup', 'pointercancel', 'lostpointercapture'].forEach(name => rows.addEventListener(name, end));
    rows.addEventListener('pointerleave', () => { if (!dragged) drag = null; });
    rows.addEventListener('click', event => {
      if (!dragged || event.detail === 0) return;
      event.preventDefault(); event.stopPropagation(); dragged = false;
    }, {capture: true});
  }
  if (typeof module !== 'undefined' && module.exports) module.exports = {analyse, describe, encode, enableColourDrag, allocateTotal};
  if (typeof document === 'undefined') return;
  const make = (tag, className, text) => {
    const el = document.createElement(tag); el.className = className;
    if (text) el.textContent = text;
    return el;
  };
  const setText = (el, text) => { if (el.textContent !== text) el.textContent = text; };
  function quantityRow(item, kind, change) {
    const row = make('div', 'pp-mix__row');
    const label = make('label', 'pp-mix__name', item.label);
    const stepper = make('div', 'pp-mix__stepper');
    const input = make('input', 'pp-mix__input');
    input.type = 'number'; input.min = '0'; input.max = '99999'; input.step = '1'; input.inputMode = 'numeric'; input.value = item.q;
    input.setAttribute('aria-label', `${item.label} ${kind === 'size' ? 'pairs' : 'quantity'}`);
    const adjust = (text, direction) => {
      const button = make('button', '', text); button.type = 'button';
      button.setAttribute('aria-label', `${direction > 0 ? 'Increase' : 'Decrease'} ${item.label} quantity`);
      button.addEventListener('click', () => {
        const value = Number(input.value) || 0;
        input.value = direction > 0 ? (kind === 'size' && value < 30 ? 30 : value + 1) : (kind === 'size' && value <= 30 ? 0 : Math.max(0, value - 1));
        input.dispatchEvent(new Event('input', {bubbles: true}));
      });
      return button;
    };
    input.addEventListener('input', () => { item.q = input.value === '' ? 0 : Number(input.value); change(); });
    input.addEventListener('keydown', event => { if (event.key === 'Enter') { event.preventDefault(); input.blur(); } });
    label.htmlFor = input.id = `pp-mix-${++quantityRow.counter}`;
    stepper.append(adjust('−', -1), input, adjust('+', 1)); row.append(label, stepper);
    return {row, input};
  }
  quantityRow.counter = 0;

  const root = document.querySelector('.pp-product-studio');
  const kind = rules[root?.dataset.productHandle];
  let ready = false;
  function initProduct() {
    if (!kind || ready) return;
    const form = root.querySelector('.product-single__form');
    const option = form?.querySelector(`[data-option="${kind === 'size' ? 'Choose Your Size' : 'Choose Your Colours'}"]`);
    const nativeInputs = [...(option?.querySelectorAll('input[type="radio"], input[type="checkbox"]') || [])];
    const quantity = form?.querySelector('input[name="quantity"]');
    const tierInputs = [...(form?.querySelectorAll('.pp-tier-item input[type="radio"]') || [])];
    if (!nativeInputs.length || !quantity || !tierInputs.length || !option.classList.contains(kind === 'colour' ? 'pp-colour-option' : 'pp-size-option')) return;
    ready = true;
    root.classList.add('pp-has-mix', 'pp-order-editor'); option.classList.add('pp-mix-option');
    const items = nativeInputs.map(input => ({value: input.value, label: sizeNames[input.value] || input.value.trim(), q: 0}));
    const initial = Math.max(kind === 'size' ? 30 : 10, Number(quantity.value) || 0);
    // Start with one clear allocation. No invented quantities for other preselected colours.
    const chosenIndex = nativeInputs.findIndex(input => input.checked);
    if (kind === 'size' || chosenIndex >= 0) items[Math.max(0, chosenIndex)].q = initial;
    const box = make('fieldset', 'pp-mix');
    const legend = make('legend', 'pp-mix__title', kind === 'size' ? 'Sizes and quantities' : 'Colours and quantities');
    const hint = make('p', 'pp-mix__hint', kind === 'size' ? 'At least 30 pairs per size. Mix sizes for a better price.' : 'Choose your colours below. Mix any combination from 10 in total.');
    const rows = make('div', 'pp-mix__rows');
    const status = make('p', 'pp-mix__status'); status.setAttribute('aria-live', 'polite');
    box.append(legend, hint, rows, status); option.append(box);
    {
      // Keep the app's original inputs connected, but give customers one editor.
      [...option.children].filter(child => child !== box).forEach(child => {
        child.hidden = true;
        child.style.setProperty('display', 'none', 'important');
      });
    }
    let syncing = false;
    let interacted = false;
    let pendingQuantity = 0;
    let fields = [];
    const totals = () => analyse(items, kind, tierInputs.map(input => Number(input.value)));
    const noun = kind === 'size' ? 'pairs' : root.dataset.productHandle.includes('beanies') ? 'beanies' : 'caps';
    root.dataset.ppOrderNoun = noun;
    if (noun === 'caps') legend.textContent = 'Choose your colours';
    const tierGroup = tierInputs[0].closest('[data-option]');
    const priceHeading = make('div', 'pp-order-prices-heading');
    const priceTitle = make('strong', '', noun === 'caps' ? 'Choose your quantity' : 'Quantity prices'); priceTitle.id = 'pp-quantity-prices-title';
    priceHeading.append(priceTitle, make('span', '', kind === 'size' ? 'Per pair, including VAT' : 'Each, including VAT'));
    tierGroup.hidden = false;
    tierGroup.setAttribute('aria-labelledby', priceTitle.id);
    if (noun === 'caps') option.before(priceHeading, tierGroup);
    else option.after(priceHeading, tierGroup);
    const sync = () => {
      if (syncing) return;
      syncing = true;
      const state = totals();
      if (state.total) pendingQuantity = 0;
      const quotedQuantity = state.total || pendingQuantity;
      const activeTier = state.tier || pendingQuantity;
      root.dataset.ppPendingQuantity = String(pendingQuantity);
      root.dataset.ppMixedTotal = String(Math.max(0, state.total));
      root.dataset.ppMixedKind = kind;
      fields.forEach(({input, item, row, add, photo}) => {
        row.classList.toggle('is-chosen', item.q > 0);
        input.value = item.q || (input === document.activeElement && input.value === '' ? '' : 0);
        if (add) {
          const editing = item.q !== 0 || document.activeElement === input;
          add.hidden = editing;
          input.parentElement.hidden = !editing;
          photo.setAttribute('aria-label', editing ? `Edit ${item.label} quantity` : `Add ${item.label}`);
          if (!editing && row.contains(document.activeElement) && document.activeElement !== photo) add.focus({preventScroll: true});
        }
        input.setCustomValidity((!Number.isSafeInteger(item.q) || item.q < 0 || item.q > 99999 || (kind === 'size' && item.q > 0 && item.q < 30)) ? state.error : '');
      });
      root.dataset.ppOrderValid = String(!state.error);
      status.classList.toggle('is-error', Boolean(state.error) && interacted);
      setText(status, pendingQuantity && !state.total ? `Choose ${kind === 'size' ? 'a size' : 'a colour'} for your ${pendingQuantity} ${noun}.` : !interacted && !state.total ? `Choose a colour to start your order.` : state.error || `${state.total} ${noun} selected · ${state.tier}+ price applied`);
      // Sizes carry no price modifier. Keep LPO's required input satisfied; the public
      // order property is replaced with the complete production breakdown at FormData.
      const first = items.findIndex(item => item.q > 0);
      nativeInputs.forEach((input, index) => {
        const checked = kind === 'size' ? index === first : items[index].q > 0;
        if (input.checked !== checked && (input.type === 'checkbox' || checked)) input.click();
      });
      if ((!state.error || (!state.total && pendingQuantity)) && Number(quantity.value) !== quotedQuantity) {
        quantity.value = quotedQuantity;
        quantity.dispatchEvent(new Event('input', {bubbles: true}));
        quantity.dispatchEvent(new Event('change', {bubbles: true}));
      }
      tierInputs.forEach(input => {
        const item = input.closest('.pp-tier-item');
        const active = Number(input.value) === activeTier;
        item.classList.toggle('pp-mix-tier-active', active);
        item.setAttribute('aria-pressed', String(active));
        input.tabIndex = -1;
        input.setAttribute('aria-hidden', 'true');
      });
      document.dispatchEvent(new CustomEvent('pp:quantitychange'));
      syncing = false;
    };
    const render = () => {
      rows.replaceChildren();
      fields = items.map(item => {
        const field = quantityRow(item, kind, () => {
          interacted = true;
          if (pendingQuantity && kind === 'size' && item.q === 30 && totals().total === item.q) item.q = pendingQuantity;
          sync();
        }); field.item = item;
        field.input.addEventListener('blur', () => {
          if (field.input.value === '') field.input.value = '0';
          sync();
        });
        if (kind === 'size') {
          field.row.classList.add('pp-sock-size');
          const native = nativeInputs[items.indexOf(item)].closest('label');
          const icon = native?.querySelector('.pp-option-icon')?.cloneNode(true);
          if (icon) field.row.prepend(icon);
          const equivalent = native?.querySelector('.pp-size-copy');
          if (equivalent) {
            field.row.querySelector('label').replaceChildren(...[...equivalent.children].map(el => el.cloneNode(true)));
          }
          const controls = make('div', 'pp-sock-quantity');
          controls.append(make('span', 'pp-sock-quantity__label', 'Pairs'), field.input.parentElement);
          field.row.append(controls);
        } else {
          field.row.classList.add('pp-colour-order');
          const photo = make('button', 'pp-colour-order__photo');
          photo.type = 'button';
          const source = nativeInputs[items.indexOf(item)].closest('label')?.querySelector('.cl-po--swatch img');
          if (source) {
            const img = make('img', '');
            img.src = source.src; img.alt = `${item.label} ${noun}`; img.loading = 'lazy'; img.decoding = 'async';
            photo.append(img);
          } else photo.append(make('span', '', item.label));
          const add = make('button', 'pp-colour-order__add', '+ Add colour');
          add.type = 'button'; add.setAttribute('aria-label', `Add ${item.label} to your order`);
          const select = () => {
            interacted = true;
            if (!item.q) { item.q = totals().total ? 1 : pendingQuantity || 10; sync(); }
            field.input.focus({preventScroll: true});
            field.input.select();
          };
          photo.addEventListener('click', select); add.addEventListener('click', select);
          field.row.prepend(photo); field.row.append(add);
          field.photo = photo; field.add = add;
        }
        rows.append(field.row); return field;
      });
      sync();
    };
    if (kind === 'colour') {
      box.classList.add('pp-mix--colours');
      rows.id = 'pp-order-colours';
      rows.setAttribute('role', 'region'); rows.setAttribute('aria-label', 'Available colours'); rows.tabIndex = 0;
      enableColourDrag(rows);
      const navigation = make('span', 'pp-order-colours-nav');
      for (const [label, direction, icon] of [['Previous colours', -1, '←'], ['Next colours', 1, '→']]) {
        const button = make('button', '', icon); button.type = 'button';
        button.setAttribute('aria-label', label); button.setAttribute('aria-controls', rows.id);
        button.addEventListener('click', () => rows.scrollBy({left: direction * rows.clientWidth * .8, behavior: 'auto'}));
        navigation.append(button);
      }
      legend.append(navigation);
      // The visible quantity editor owns allocations. Late app change events on
      // its hidden swatches must not erase quantities the customer has entered.
    }
    // Route tile clicks through the allocation editor so LPO receives one consistent total.
    tierGroup.classList.add('pp-mix-tiers');
    const chooseTier = event => {
      const tile = event.target.closest('.pp-tier-item');
      if (!tile || (event.type === 'keydown' && !['Enter', ' '].includes(event.key))) return;
      event.preventDefault(); event.stopImmediatePropagation();
      if (syncing) return;
      const target = Number(tile.querySelector('input[type="radio"]').value);
      interacted = true;
      const selected = items.filter(item => item.q > 0);
      if (!selected.length) { pendingQuantity = target; sync(); return; }
      const allocation = allocateTotal(items, kind, target);
      if (!allocation) {
        setText(status, `Choose at least ${Math.max(kind === 'size' ? 30 : 10, selected.length * (kind === 'size' ? 30 : 1))} ${noun} for your ${selected.length} selected ${kind === 'size' ? 'sizes' : 'colours'}, or remove a selection first.`);
        status.classList.add('is-error'); return;
      }
      items.forEach((item, index) => { item.q = allocation[index]; });
      sync();
    };
    tierInputs.forEach(input => {
      const tile = input.closest('.pp-tier-item');
      tile.setAttribute('role', 'button'); tile.tabIndex = 0;
      tile.setAttribute('aria-label', `Choose ${input.value} ${noun}`);
    });
    tierGroup.addEventListener('click', chooseTier, true);
    tierGroup.addEventListener('keydown', chooseTier, true);
    const tierHeading = root.querySelector('.pp-tier-label, .pp-tier-heading');
    if (tierHeading) setText(tierHeading, 'Price by total quantity');
    const validate = event => {
      const state = totals();
      if (!state.error) return;
      event.preventDefault(); event.stopImmediatePropagation(); interacted = true; sync();
      const field = fields.find(field => !field.input.checkValidity()) || fields.find(field => !field.row.hidden);
      if (field) {
        field.row.hidden = false;
        const target = field.input.parentElement.hidden ? field.add : field.input;
        target?.focus();
        if (target === field.input) field.input.reportValidity();
      }
    };
    form.addEventListener('submit', validate, true);
    form.addEventListener('click', event => {
      if (event.target.closest('button[name="add"]')) validate(event);
    }, true);
    form.addEventListener('reset', () => requestAnimationFrame(() => {
      pendingQuantity = 0;
      items.forEach(item => { item.q = 0; });
      render();
    }));
    form.addEventListener('formdata', event => {
      const state = totals();
      if (state.error) return;
      event.formData.set('quantity', state.total);
      event.formData.delete(`properties[${kind === 'size' ? 'Choose Your Size' : 'Choose Your Colours'}]`);
      event.formData.set(`properties[${property(kind)}]`, describe(items, kind));
      // A second configured run must not merge into a cart line with a stale split.
      event.formData.set('properties[_pp_mix]', encode(items, kind, crypto.randomUUID()));
    });
    render();
    if (kind === 'colour' && items.length > 6) {
      const tools = make('div', 'pp-order-colour-tools');
      const searchWrap = make('div', 'pp-order-search');
      const searchIcon = make('span', 'pp-order-search__icon'); searchIcon.setAttribute('aria-hidden', 'true');
      searchIcon.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"><circle cx="10.5" cy="10.5" r="6.5"/><path d="m15.5 15.5 4.5 4.5"/></svg>';
      const search = make('input', 'pp-order-colour-search');
      search.type = 'search'; search.placeholder = 'Search colours';
      search.setAttribute('aria-label', 'Find a colour for your order'); search.autocomplete = 'off';
      searchWrap.append(searchIcon, search);
      tools.append(searchWrap, make('span', 'pp-order-colour-count', `${items.length} colours`));
      search.addEventListener('keydown', event => { if (event.key === 'Enter') event.preventDefault(); });
      const empty = make('p', 'pp-order-colours-empty', 'No colours match. Try another name.'); empty.hidden = true;
      const filter = () => {
        const query = search.value.trim().toLowerCase();
        let found = 0;
        fields.forEach(field => {
          const invalid = !Number.isSafeInteger(field.item.q) || field.item.q < 0 || field.item.q > 99999;
          const match = invalid || !query || field.item.label.toLowerCase().includes(query);
          field.row.hidden = !match; if (match) found++;
        });
        rows.scrollLeft = 0; empty.hidden = found > 0;
      };
      search.addEventListener('input', filter);
      rows.before(tools); rows.after(empty); filter();
    }
  }
  if (kind) {
    const observer = new MutationObserver(() => { initProduct(); if (ready) observer.disconnect(); });
    observer.observe(root, {childList: true, subtree: true}); initProduct();
  }

  const cartPending = () => Boolean(window.theme?.cart?.pendingChange);
  function setMixedPending(pending) {
    document.querySelectorAll('[data-pp-cart-mix] input, [data-pp-cart-mix] button:not([data-pp-cart-refresh])').forEach(el => { el.disabled = pending; });
    document.querySelectorAll('.cart__item-remove').forEach(el => {
      if (pending) el.setAttribute('aria-disabled', 'true');
      else el.removeAttribute('aria-disabled');
    });
  }
  document.addEventListener('cart:pending', event => setMixedPending(Boolean(event.detail)));
  document.addEventListener('click', event => {
    if (cartPending() && event.target.closest('.cart__item-remove')) {
      event.preventDefault(); event.stopImmediatePropagation();
    }
  }, true);
  async function readCurrentCart(base) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 15000);
    try {
      const response = await fetch(`${base}cart.js?t=${Date.now()}`, {credentials: 'same-origin', cache: 'no-store', headers: {Accept: 'application/json'}, signal: controller.signal});
      if (!response.ok) throw new Error('Could not load your basket.');
      const cart = await response.json();
      if (!Array.isArray(cart.items) || cart.status) throw new Error('Could not confirm your basket.');
      return cart;
    } finally { clearTimeout(timer); }
  }
  async function refreshCartMarkup() {
    const rebuilding = [];
    document.dispatchEvent(new CustomEvent('cart:build', {detail: {waitUntil: promise => rebuilding.push(promise)}}));
    if (!rebuilding.length || (await Promise.all(rebuilding)).some(result => result !== true)) {
      throw new Error('Your basket could not be refreshed.');
    }
  }

  // Edit the same configured order in the cart; preserve all artwork and LPO properties.
  document.addEventListener('click', async event => {
    const button = event.target.closest('[data-pp-mix-save]');
    if (!button || button.disabled) return;
    const editor = button.closest('[data-pp-cart-mix]');
    const status = editor.querySelector('[data-pp-mix-status]');
    if (cartPending()) { status.textContent = 'Please wait while your basket updates.'; return; }
    const api = window.theme?.cart;
    if (!api || typeof api._updateCart !== 'function') { status.textContent = 'Please reload your basket before editing quantities.'; return; }
    const kind = editor.dataset.kind;
    const items = [...editor.querySelectorAll('.pp-mix__input')].map(input => ({value: input.dataset.value, label: input.dataset.label, q: Number(input.value)}));
    const state = analyse(items, kind);
    if (state.error) { status.textContent = state.error; status.classList.add('is-error'); return; }
    const form = editor.closest('form');
    const base = window.Shopify?.routes?.root || '/';
    const key = editor.closest('[data-key]').dataset.key;
    let runId, desired, variant, message = '', isError = false;
    api.pendingChange = true;
    document.dispatchEvent(new CustomEvent('cart:pending', {detail: true}));
    status.textContent = 'Updating your order…'; status.classList.remove('is-error');

    const currentEditor = () => [...form.querySelectorAll('[data-pp-cart-mix]')].find(el => {
      try { return runId && JSON.parse(el.dataset.mix).id === runId; } catch (_) { return false; }
    }) || form.querySelector(`[data-key="${CSS.escape(key)}"] [data-pp-cart-mix]`);
    const report = (text, error, retry) => {
      const target = currentEditor()?.querySelector('[data-pp-mix-status]') || form.querySelector('[data-cart-status]') || status;
      target.textContent = text; target.hidden = false; target.classList.toggle('is-error', error);
      if (retry) target.append(retry);
    };
    const reconcile = async () => {
      const cart = await readCurrentCart(base);
      await refreshCartMarkup();
      const saved = desired && cart.items.find(item => (item.variant_id || item.id) === variant && item.quantity === state.total && Object.entries(desired).every(([name, value]) => item.properties?.[name] === value));
      if (desired) {
        message = saved ? 'Quantities saved.' : message || 'Your saved quantities were refreshed. Review them before saving again.';
        isError = !saved;
      }
      document.dispatchEvent(new CustomEvent('cart:updated', {detail: {cart}}));
      const restored = currentEditor();
      if (restored) { restored.open = true; initCartEditor(restored); }
      api.pendingChange = false;
      document.dispatchEvent(new CustomEvent('cart:pending', {detail: false}));
      report(message, isError);
      restored?.querySelector('[data-pp-mix-save]')?.focus({preventScroll: true});
    };
    const recover = () => {
      const restored = currentEditor();
      if (restored) { restored.open = true; initCartEditor(restored); }
      const retry = make('button', '', 'Refresh basket'); retry.type = 'button'; retry.setAttribute('data-pp-cart-refresh', '');
      retry.addEventListener('click', async () => {
        if (retry.disabled) return;
        retry.disabled = true;
        try { await reconcile(); } catch (_) { recover(); }
      });
      report('We could not confirm the saved basket. Refresh it before checkout or another quantity change.', true, retry);
      retry.focus({preventScroll: true});
    };
    try {
      const cart = await readCurrentCart(base);
      const line = cart.items.find(item => item.key === key);
      if (!line) throw new Error('This item has changed. Refresh your basket before editing it.');
      runId = JSON.parse(line.properties._pp_mix).id || crypto.randomUUID();
      variant = line.variant_id || line.id;
      desired = {...line.properties, [property(kind)]: describe(items, kind), _pp_mix: encode(items, kind, runId)};
      // LPO copies this snapshot into checkout bundle components. Keep its
      // production breakdown aligned with the parent after cart edits.
      if (Object.prototype.hasOwnProperty.call(desired, '_cl_options_json')) {
        let snapshot;
        try { snapshot = JSON.parse(desired._cl_options_json); } catch (_) {}
        if (!snapshot || typeof snapshot !== 'object' || Array.isArray(snapshot)) {
          throw new Error('This item could not be updated safely. Remove it and add it again before checkout.');
        }
        snapshot[property(kind)] = desired[property(kind)];
        snapshot._pp_mix = desired._pp_mix;
        desired._cl_options_json = JSON.stringify(snapshot);
      }
      const updated = JSON.parse(await api._updateCart({url: `${base}cart/change.js?t=${Date.now()}`, data: JSON.stringify({id: line.key, quantity: state.total, properties: desired})}));
      if (updated.status || !Array.isArray(updated.items)) throw new Error(typeof updated.description === 'string' ? updated.description : 'The basket could not be updated.');
    } catch (error) { message = error.message; isError = true; }
    // A lost reply can follow a committed change. Only a fresh read and rendered
    // basket release the existing pending guard; retry never repeats the write.
    try { await reconcile(); } catch (_) { recover(); }
  });
  function initCartEditor(editor) {
    if (!editor.open || editor.dataset.ready) return;
    try {
      const saved = JSON.parse(editor.dataset.mix);
      if (saved.v !== 1 || !['size', 'colour'].includes(saved.kind) || !Array.isArray(saved.items)) return;
      editor.dataset.ready = 'true'; editor.dataset.kind = saved.kind;
      const status = editor.querySelector('[data-pp-mix-status]');
      const rows = editor.querySelector('.pp-mix__rows');
      saved.items.forEach(item => {
        const field = quantityRow(item, saved.kind, () => {
          const state = analyse(saved.items, saved.kind);
          status.textContent = state.error || `${state.total} ${saved.kind === 'size' ? 'pairs' : 'items'} · save to update your basket`;
          status.classList.toggle('is-error', Boolean(state.error));
        });
        field.input.dataset.value = item.value; field.input.dataset.label = item.label; rows.append(field.row);
      });
      if (cartPending()) setMixedPending(true);
    } catch (_) { editor.querySelector('[data-pp-mix-status]').textContent = 'Please refresh your basket to edit quantities.'; }
  }
  document.addEventListener('toggle', event => {
    if (event.target.matches('[data-pp-cart-mix]')) initCartEditor(event.target);
  }, true);
})();
